<template>
  <div>
    <DefaultCrudView
      ref="crudView"
      :breadcrumbsItems="breadcrumbs"
      :actions="actionButtons"
      @create-new="newLane"
    >
      <template v-slot:content>
        <v-data-table
          :headers="headers"
          :items="lanes"
          :loading="loading"
          :server-items-length="itemsLength"
          :options.sync="options"
          :footer-props="$defaultDataTableFooterProps"
          class="elevation-1"
        >
          <template v-slot:[`item.lane_title`]="{ item }">
            {{ item.lane_title || '-' }}
          </template>

          <template v-slot:[`item.executors`]="{ item }">
            <div
              :ref="`executors-${item.lane_id}`"
              class="executors-container"
            >
              <template v-if="getLaneExecutors(item.lane_id).length === 0">
                <v-chip small outlined>
                  {{ $t('no_executors') }}
                </v-chip>
              </template>
              <template v-else>
                <v-chip
                  v-for="executor in getVisibleExecutors(item.lane_id)"
                  :key="executor.id"
                  small
                  class="mr-1 mb-0"
                >
                  <v-icon small left>
                    {{ executor.isGroup ? 'mdi-account-multiple' : 'mdi-account' }}
                  </v-icon>
                  {{ executor.name }}
                </v-chip>
                <v-chip
                  v-if="getHiddenExecutorsCount(item.lane_id) > 0"
                  small
                  outlined
                  class="mb-0"
                  :title="getHiddenExecutorsTooltip(item.lane_id)"
                >
                  +{{ getHiddenExecutorsCount(item.lane_id) }}
                </v-chip>
              </template>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              @click="editLane(item)"
              icon
            >
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn
              @click="deleteLane(item)"
              icon
            >
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </template>
    </DefaultCrudView>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t('confirm_action') }}
        </v-card-title>
        <v-card-text>
          {{ confirmDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="confirmDialog.show = false"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="error"
            text
            @click="confirmAction"
          >
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Lane Modal -->
    <DefaultModal
      :title="getFormModalTitle()"
      ref="laneModal"
      @submit="saveLane"
      :width="800"
    >
    <template v-slot:content>
      <v-form ref="laneForm" v-model="formValid">
        <v-text-field
          v-model="laneForm.title"
          :label="$t('lane_title')"
          :rules="[v => !!v || $t('field_required')]"
          outlined
          dense
        ></v-text-field>

        <v-divider class="my-4"></v-divider>

        <UserMultiSelect
          v-model="selectedUsersAndGroups"
          :items="normalizedUsersAndGroups"
          :loading="loadingUsersGroupsList"
          :label="$t('add_users_and_groups')"
          item-value="id"
        />

        <v-list v-if="laneExecutors.length > 0" class="mt-4">
          <v-subheader>{{ $t('current_executors') }}</v-subheader>
          <v-list-item
            v-for="executor in laneExecutors"
            :key="executor.lane_executor_id"
          >
            <v-list-item-avatar :class="executor.user_group_id ? 'group-bg' : 'avatar'">
              <template v-if="executor.users_id">
                <img
                  v-if="getUserAvatarUrl(executor.users_id)"
                  :src="getUserAvatarUrl(executor.users_id)"
                  :alt="`${executor.users_id} avatar`"
                >
                <span v-else class="white--text text-uppercase">
                  {{ getUserName(executor.users_id)[0] }}
                </span>
              </template>
              <v-icon v-else color="white">mdi-account-multiple</v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title>
                {{ executor.users_id ? getUserName(executor.users_id) : getGroupTitle(executor.user_group_id) }}
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-icon>
              <v-btn
                icon
                color="grey darken-2"
                @click="removeLaneExecutor(executor)"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </v-form>
    </template>
    </DefaultModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import UserMultiSelect from '@/components/core/UserMultiSelect.vue'

import AppActions from '@/store/app/actions-types'
import UserActions from '@/store/core/user/actions-types'
import GroupActions from '@/store/core/group/actions-types'

import LaneAPI from '@/services/api/job/lane'
import LaneExecutorAPI from '@/services/api/job/lane_executor'
import GroupAPI from '@/services/api/core/group'
import UserAPI from '@/services/api/core/user'

export default {
  name: "LaneList",

  components: {
    DefaultCrudView,
    DefaultModal,
    UserMultiSelect,
  },

  data() {
    return {
      loading: false,
      lanes: [],
      laneExecutors: [],
      allLaneExecutors: [],
      selectedLane: null,
      formValid: false,
      laneForm: {
        title: '',
      },
      loadingUsersGroupsList: false,
      selectedUsersAndGroups: [],
      usersAndGroups: [],
      options: {
        sortBy: ['lane_title'],
        sortDesc: [false],
        page: 1,
        itemsPerPage: 10,
      },
      itemsLength: 0,
      confirmDialog: {
        show: false,
        message: '',
        action: null,
        data: null,
      },
      visibleExecutorsMap: {},
    }
  },

  computed: {
    ...mapGetters('app', [
      'getUserApplicationById'
    ]),

    poolId() {
      return this.$route.params.poolId
    },

    poolTitle() {
      return this.$route.query.poolTitle || ''
    },

    pageTitle() {
      return this.$t('lanes_in_pool', { pool: this.poolTitle })
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('pools'),
          disabled: false,
          iconColor: 'primary',
          prependIcon: 'mdi-view-sequential',
          to: {
            name: "JobPools",
            params: { appId: this.$route.params.appId }
          },
          exact: true,
          ripple: true,
        },
        {
          text: this.pageTitle,
        }
      ]
    },

    headers() {
      return [
        { text: this.$t('lane_title'), value: 'lane_title', sortable: true },
        { text: this.$t('executors'), value: 'executors', sortable: false, width: '50%' },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '120px' }
      ]
    },

    actionButtons() {
      return [
        {
          text: "create_lane",
          eventToEmit: "create-new",
          icon: "mdi-plus",
          color: "primary"
        }
      ]
    },

    appInstance() {
      return this.getUserApplicationById(this.$route.params.appId)
    },

    appConfig() {
      if (!this.appInstance || !this.appInstance.config) return {}
      return this.parseApplicationConfig(this.appInstance.config)
    },

    appTenantId() {
      return (
        this.appConfig.tenant_id ||
        this.appConfig.tenantId ||
        (this.appConfig.tenant && (this.appConfig.tenant.id || this.appConfig.tenant.tenant_id)) ||
        null
      )
    },

    normalizedUsersAndGroups() {
      return this.usersAndGroups.reduce((result, el) => {
        if (el.users_id) {
          if (this.laneExecutors.find(le => le.users_id == el.users_id))
            return result
        }

        if (el.user_group_id) {
          if (!this.isTenantLaneGroup(el))
            return result

          if (this.laneExecutors.find(le => le.user_group_id == el.user_group_id))
            return result
        }

        result.push({
          id: el.users_id ? `users-${el.users_id}` : `group-${el.user_group_id}`,
          idRaw: el.users_id || el.user_group_id,
          name: el.name || el.group_title,
          isUser: el.users_id ? true : false,
          isGroup: el.user_group_id ? true : false,
          email: el.email ? el.email : null
        })

        return result
      }, [])
    },
  },

  watch: {
    options: {
      handler() {
        this.loadLanes()
      },
      deep: true,
    },
    appTenantId(newTenantId, oldTenantId) {
      if (newTenantId && newTenantId !== oldTenantId) {
        this.getUsersAndGroups()
      }
    },
  },

  mounted() {
    this.loadLanes()
    this.getUsersAndGroups()

    // Recalculate on window resize
    window.addEventListener('resize', this.calculateVisibleExecutors)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateVisibleExecutors)
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    ...mapActions('core/user', [
      UserActions.GET_ALL_USERS,
    ]),

    ...mapActions('core/group', [
      GroupActions.GET_ALL_GROUPS,
    ]),

    parseApplicationConfig(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        return {}
      }
    },

    async loadLanes() {
      this.loading = true

      try {
        if (!this.poolId || this.poolId === 'null') {
          console.warn('Invalid poolId:', this.poolId)
          this.lanes = []
          this.itemsLength = 0
          return
        }

        const result = await LaneAPI.getLanesFromPool(this.poolId)
        this.lanes = result.data.items || []
        this.itemsLength = result.data.total || this.lanes.length

        // Load all lane executors for display
        await this.loadAllLaneExecutors()
      } catch (error) {
        console.error('Error loading lanes:', error)
        this.lanes = []
        this.itemsLength = 0
      } finally {
        this.loading = false
      }
    },

    async loadAllLaneExecutors() {
      try {
        const executorPromises = this.lanes.map(lane =>
          LaneExecutorAPI.getLaneExecutorsFromLane(lane.lane_id)
            .then(result => ({
              laneId: lane.lane_id,
              executors: result.data || []
            }))
            .catch(() => ({ laneId: lane.lane_id, executors: [] }))
        )

        const results = await Promise.all(executorPromises)
        this.allLaneExecutors = results
        if (this.usersAndGroups.length > 0) {
          await this.loadMissingExecutorNames()
        }

        // Calculate visible executors after data is loaded
        this.calculateVisibleExecutors()
      } catch (error) {
        console.error('Error loading lane executors:', error)
        this.allLaneExecutors = []
      }
    },

    getLaneExecutors(laneId) {
      const laneData = this.allLaneExecutors.find(le => le.laneId === laneId)
      if (!laneData) return []

      return laneData.executors.map(executor => ({
        id: executor.lane_executor_id,
        name: executor.users_id ? this.getUserName(executor.users_id) : this.getGroupTitle(executor.user_group_id),
        isUser: !!executor.users_id,
        isGroup: !!executor.user_group_id,
      }))
    },

    getVisibleExecutors(laneId) {
      const allExecutors = this.getLaneExecutors(laneId)
      // Default to showing only 2 executors if not yet calculated
      const visibleCount = this.visibleExecutorsMap[laneId] !== undefined
        ? this.visibleExecutorsMap[laneId]
        : Math.min(2, allExecutors.length)
      return allExecutors.slice(0, visibleCount)
    },

    getHiddenExecutorsCount(laneId) {
      const allExecutors = this.getLaneExecutors(laneId)
      const visibleCount = this.visibleExecutorsMap[laneId] !== undefined
        ? this.visibleExecutorsMap[laneId]
        : Math.min(2, allExecutors.length)
      return Math.max(0, allExecutors.length - visibleCount)
    },

    getHiddenExecutorsTooltip(laneId) {
      const allExecutors = this.getLaneExecutors(laneId)
      const visibleCount = this.visibleExecutorsMap[laneId] !== undefined
        ? this.visibleExecutorsMap[laneId]
        : Math.min(2, allExecutors.length)
      const hiddenExecutors = allExecutors.slice(visibleCount)
      return hiddenExecutors.map(e => e.name).join(', ')
    },

    calculateVisibleExecutors() {
      this.$nextTick(() => {
        this.lanes.forEach(lane => {
          const laneId = lane.lane_id
          const containerRef = this.$refs[`executors-${laneId}`]

          if (!containerRef || !containerRef[0]) return

          const container = containerRef[0]
          const allExecutors = this.getLaneExecutors(laneId)

          if (allExecutors.length === 0) return
          if (allExecutors.length === 1) {
            this.$set(this.visibleExecutorsMap, laneId, 1)
            return
          }

          // Temporarily show all chips to measure
          this.$set(this.visibleExecutorsMap, laneId, allExecutors.length)

          this.$nextTick(() => {
            const containerWidth = container.offsetWidth

            if (containerWidth === 0) {
              // Container not yet rendered, try again later
              setTimeout(() => this.calculateVisibleExecutors(), 100)
              return
            }

            const chips = container.querySelectorAll('.v-chip')

            if (chips.length === 0) return

            let totalWidth = 0
            let visibleCount = 0
            const counterChipWidth = 70 // Approximate width for "+N" chip
            const margin = 4 // margin-right on chips

            for (let i = 0; i < chips.length; i++) {
              const chipWidth = chips[i].offsetWidth + margin

              // Check if we need to account for counter chip
              const willNeedCounter = (i < allExecutors.length - 1)
              const widthWithChip = totalWidth + chipWidth
              const widthWithCounter = widthWithChip + (willNeedCounter ? counterChipWidth : 0)

              if (widthWithCounter <= containerWidth) {
                totalWidth = widthWithChip
                visibleCount = i + 1
              } else {
                break
              }
            }

            // Ensure at least one chip is visible if there's any space
            if (visibleCount === 0 && allExecutors.length > 0) {
              visibleCount = 1
            }

            // If we can show all, show all (no counter needed)
            if (visibleCount === allExecutors.length) {
              this.$set(this.visibleExecutorsMap, laneId, allExecutors.length)
            } else {
              // Otherwise limit to visible count
              this.$set(this.visibleExecutorsMap, laneId, Math.max(1, visibleCount))
            }
          })
        })
      })
    },

    async getUsersAndGroups() {
      const tenantId = this.appTenantId

      if (!tenantId) {
        this.usersAndGroups = []
        return
      }

      this.loadingUsersGroupsList = true
      this.usersAndGroups = []

      const usersOptions = {
        'filter[tenant_id]': tenantId,
        'filter[include_foreign]': true,
        'filter[active]': true,
        'limit': 9999,
      }

      const groupsOptions = {
        'filter[tenant]': tenantId,
        'filter[include_foreign]': true,
        'filter[active]': true,
        'limit': 9999,
      }

      try {
        const [usersResult, groupsResult] = await Promise.all([
          this[UserActions.GET_ALL_USERS](usersOptions),
          this[GroupActions.GET_ALL_GROUPS](groupsOptions),
        ])

        this.usersAndGroups = [
          ...(usersResult.items || []),
          ...(groupsResult.items || []),
        ]

        await this.loadMissingExecutorNames()
      } finally {
        this.loadingUsersGroupsList = false
      }
    },

    async loadMissingExecutorNames() {
      const knownGroupIds = new Set(
        this.usersAndGroups
          .filter(item => item.user_group_id)
          .map(item => String(item.user_group_id))
      )
      const knownUserIds = new Set(
        this.usersAndGroups
          .filter(item => item.users_id)
          .map(item => String(item.users_id))
      )
      const missingGroupIds = new Set()
      const missingUserIds = new Set()

      this.allLaneExecutors.forEach(laneData => {
        const executors = laneData.executors || []

        executors.forEach(executor => {
          if (executor.user_group_id && !knownGroupIds.has(String(executor.user_group_id))) {
            missingGroupIds.add(executor.user_group_id)
          }

          if (executor.users_id && !knownUserIds.has(String(executor.users_id))) {
            missingUserIds.add(executor.users_id)
          }
        })
      })

      const [groups, users] = await Promise.all([
        Promise.all(Array.from(missingGroupIds).map(async groupId => {
          try {
            const result = await GroupAPI.getUserGroup(groupId)
            return result.data || null
          } catch (error) {
            console.warn('Unable to load lane executor group:', groupId, error)
            return null
          }
        })),
        Promise.all(Array.from(missingUserIds).map(async userId => {
          try {
            const result = await UserAPI.getUser(userId)
            return result.data || null
          } catch (error) {
            console.warn('Unable to load lane executor user:', userId, error)
            return null
          }
        })),
      ])

      const missingItems = [...groups, ...users].filter(Boolean)
      const existingKeys = new Set(
        this.usersAndGroups
          .map(item => this.getUsersAndGroupsItemKey(item))
          .filter(Boolean)
      )
      const newItems = missingItems.filter(item => {
        const key = this.getUsersAndGroupsItemKey(item)

        if (!key || existingKeys.has(key)) {
          return false
        }

        existingKeys.add(key)
        return true
      })

      if (newItems.length > 0) {
        this.usersAndGroups = [...this.usersAndGroups, ...newItems]
      }
    },

    getUsersAndGroupsItemKey(item) {
      if (item.user_group_id) {
        return `group-${item.user_group_id}`
      }

      if (item.users_id) {
        return `user-${item.users_id}`
      }

      return null
    },

    isTenantLaneGroup(group) {
      const tenantId = this.appTenantId
      const groupTenantId = group.tenant_id || group.tenantId

      return (
        tenantId &&
        groupTenantId &&
        String(groupTenantId) === String(tenantId) &&
        this.getApplicationInstanceIds(group).length === 0
      )
    },

    getApplicationInstanceIds(group) {
      const ids = group.application_instance_ids || group.applicationInstanceIds || []

      if (Array.isArray(ids)) {
        return ids.filter(id => id !== null && id !== undefined && String(id) !== '')
      }

      if (typeof ids === 'string') {
        return ids
          .split(',')
          .map(id => id.trim())
          .filter(id => id !== '')
      }

      return []
    },

    async loadLaneExecutors() {
      if (!this.selectedLane) {
        this.laneExecutors = []
        return
      }

      try {
        const result = await LaneExecutorAPI.getLaneExecutorsFromLane(this.selectedLane.lane_id)
        this.laneExecutors = result.data || []
      } catch (error) {
        console.error('Error loading lane executors:', error)
        this.laneExecutors = []
      }
    },

    async newLane() {
      this.selectedLane = null
      this.laneForm = {
        title: '',
      }
      this.laneExecutors = []
      this.selectedUsersAndGroups = []

      this.$refs.laneModal.openModal()
    },

    async editLane(lane) {
      this.selectedLane = lane
      this.laneForm = {
        title: lane.lane_title || '',
      }
      this.selectedUsersAndGroups = []

      await this.loadLaneExecutors()
      this.$refs.laneModal.openModal()
    },

    async saveLane() {
      if (!this.$refs.laneForm.validate()) {
        this.$refs.laneModal.submitting = false
        return
      }

      this.$refs.laneModal.errorMessage = null
      this.$refs.laneModal.errorList = []

      try {
        const formData = {
          pool_id: this.poolId,
          lane_title: this.laneForm.title,
        }

        let lane
        if (this.selectedLane) {
          await LaneAPI.updateLane(this.selectedLane.lane_id, formData)
          lane = { ...this.selectedLane, ...formData }
        } else {
          const result = await LaneAPI.createLane(formData)
          lane = result.data
        }

        // Add new executors
        if (this.selectedUsersAndGroups.length > 0) {
          await this.addExecutors(lane.lane_id)
        }

        const message = this.selectedLane
          ? this.$t('lane_updated_successfully')
          : this.$t('lane_created_successfully')
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)

        this.$refs.laneModal.closeModal()
        this.loadLanes()
      } catch (error) {
        this.$refs.laneModal.submitting = false
        if (error.response && error.response.data) {
          this.$refs.laneModal.errorMessage = error.response.data.message || this.$t('error_occurred')
        }
      }
    },

    async addExecutors(laneId) {
      const executors = []

      this.selectedUsersAndGroups.forEach(id => {
        const item = this.normalizedUsersAndGroups.find(el => el.id == id)

        executors.push({
          lane_id: laneId,
          user_group_id: item.isGroup ? item.idRaw : null,
          users_id: item.isUser ? item.idRaw : null,
        })
      })

      // Add executors one by one since bulk endpoint doesn't exist
      if (executors.length > 0) {
        const promises = executors.map(executor =>
          LaneExecutorAPI.createLaneExecutor(executor)
        )
        await Promise.all(promises)
      }
    },

    confirmAction() {
      if (this.confirmDialog.action === 'deleteLane') {
        this.executeDeleteLane(this.confirmDialog.data)
      } else if (this.confirmDialog.action === 'removeLaneExecutor') {
        this.executeRemoveLaneExecutor(this.confirmDialog.data)
      }
      this.confirmDialog.show = false
    },

    async removeLaneExecutor(executor) {
      this.confirmDialog.message = this.$t('confirm_delete')
      this.confirmDialog.action = 'removeLaneExecutor'
      this.confirmDialog.data = executor
      this.confirmDialog.show = true
    },

    async executeRemoveLaneExecutor(executor) {
      const deleteData = {
        user_group_id: executor.user_group_id ?? null,
        users_id: executor.users_id ?? null
      }

      try {
        await LaneExecutorAPI.deleteLaneExecutor(executor.lane_id, deleteData)
        await this.loadLaneExecutors()
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('executor_removed_successfully'))
      } catch (error) {
        console.error('Error removing executor:', error)
      }
    },

    deleteLane(lane) {
      this.confirmDialog.message = this.$t('confirm_delete')
      this.confirmDialog.action = 'deleteLane'
      this.confirmDialog.data = lane
      this.confirmDialog.show = true
    },

    executeDeleteLane(lane) {
      LaneAPI.deleteLane(lane.lane_id)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('lane_deleted_successfully'))
          this.loadLanes()
        })
        .catch((error) => {
          console.error('Error deleting lane:', error)
        })
    },

    getFormModalTitle() {
      const action = this.selectedLane ? this.$t('edit') : this.$t('add')
      return `${action} ${this.$t('lane')}`
    },

    getUserAvatarUrl(userId) {
      return (this.usersAndGroups.find(el => el.users_id == userId)?.avatar_url) ?? null
    },

    getUserName(userId) {
      return (this.usersAndGroups.find(el => el.users_id == userId)?.name) ?? `${this.$t('user')} ID: ${userId}`
    },

    getGroupTitle(groupId) {
      return (this.usersAndGroups.find(el => el.user_group_id == groupId)?.group_title) ?? `${this.$t('group')} ID: ${groupId}`
    },
  }
}
</script>

<style scoped>
.avatar {
  background-color: var(--v-primary-base) !important;
}

.group-bg {
  background-color: var(--v-primary-base) !important;
}

.executors-container {
  display: inline-flex;
  flex-wrap: nowrap !important;
  overflow: hidden;
  align-items: center;
  max-width: 100%;
}

.executors-container .v-chip {
  flex-shrink: 0;
  white-space: nowrap;
}
</style>
